import { useCallback, useEffect, useRef, useState } from 'react';
import {
  audioBlobTypeFromMime,
  createPitchCameraCompositor,
  pickAudioRecorderMimeType,
  pickVideoRecorderMimeType,
} from '@/lib/pitch/pitchCameraCompositor';
import {
  PITCH_RECORD_HEIGHT,
  PITCH_RECORD_VIDEO_BITRATE,
  PITCH_RECORD_WIDTH,
} from '@/lib/pitch/pitchRecordQuality';

const STOP_FINALIZE_TIMEOUT_MS = 3000;

/**
 * Record audio (required) and optional camera video as separate blobs.
 * With a background template, MediaPipe segments the founder and composites
 * the person cutout over the template (live preview + recorded video).
 */
export function usePitchRecorder({ withCamera = false, backgroundImageUrl = '' } = {}) {
  const [recording, setRecording] = useState(false);
  const [previewStream, setPreviewStream] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState(null);
  const audioRecorderRef = useRef(null);
  const videoRecorderRef = useRef(null);
  const audioStreamRef = useRef(null);
  const videoStreamRef = useRef(null);
  const compositorRef = useRef(null);
  const previewGenRef = useRef(0);
  const audioChunksRef = useRef([]);
  const videoChunksRef = useRef([]);
  const audioMimeRef = useRef('');
  const startedAtRef = useRef(0);

  const stopPreview = useCallback(() => {
    compositorRef.current?.stop?.();
    compositorRef.current = null;
    videoStreamRef.current?.getTracks?.().forEach((t) => t.stop());
    videoStreamRef.current = null;
    setPreviewStream(null);
  }, []);

  const stopAudioRecording = useCallback(() => {
    audioStreamRef.current?.getTracks?.().forEach((t) => t.stop());
    audioStreamRef.current = null;
    audioRecorderRef.current = null;
    videoRecorderRef.current = null;
    audioMimeRef.current = '';
  }, []);

  useEffect(() => {
    if (!withCamera) {
      stopPreview();
      setPreviewLoading(false);
      return undefined;
    }

    const gen = ++previewGenRef.current;
    let cancelled = false;
    const bgUrl = String(backgroundImageUrl || '').trim();

    (async () => {
      setPreviewLoading(true);
      setError(null);
      try {
        const videoStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'user',
            width: { ideal: PITCH_RECORD_WIDTH },
            height: { ideal: PITCH_RECORD_HEIGHT },
          },
          audio: false,
        });
        if (cancelled || previewGenRef.current !== gen) {
          videoStream.getTracks().forEach((t) => t.stop());
          return;
        }

        videoStreamRef.current = videoStream;

        if (bgUrl) {
          const compositor = await createPitchCameraCompositor({
            videoStream,
            backgroundImageUrl: bgUrl,
          });
          if (cancelled || previewGenRef.current !== gen) {
            compositor.stop();
            videoStream.getTracks().forEach((t) => t.stop());
            return;
          }
          compositorRef.current = compositor;
          setPreviewStream(compositor.stream);
        } else {
          setPreviewStream(videoStream);
        }
      } catch (err) {
        if (cancelled || previewGenRef.current !== gen) return;
        console.error('Pitch camera preview failed:', err);
        setError(err?.message || 'Camera or background cutout failed');
        stopPreview();
      } finally {
        if (!cancelled && previewGenRef.current === gen) {
          setPreviewLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      stopPreview();
    };
  }, [withCamera, backgroundImageUrl, stopPreview]);

  const start = useCallback(async () => {
    setError(null);
    audioChunksRef.current = [];
    videoChunksRef.current = [];
    try {
      const audioStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
      audioStreamRef.current = audioStream;

      const audioMime = pickAudioRecorderMimeType();
      audioMimeRef.current = audioMime;
      const audioRecorder = new MediaRecorder(
        audioStream,
        audioMime ? { mimeType: audioMime } : undefined,
      );
      audioRecorder.ondataavailable = (e) => {
        if (e.data?.size) audioChunksRef.current.push(e.data);
      };
      audioRecorder.onerror = (e) => {
        console.error('Pitch audio recorder error:', e);
      };
      audioRecorderRef.current = audioRecorder;
      audioRecorder.start(200);

      if (withCamera) {
        const compositor = compositorRef.current;
        let recordStream = videoStreamRef.current;
        try {
          if (typeof compositor?.openRecordStream === 'function') {
            recordStream = compositor.openRecordStream();
          } else {
            recordStream = compositor?.stream || videoStreamRef.current;
          }
        } catch (streamErr) {
          console.warn('Pitch record stream refresh failed; using preview stream', streamErr);
          recordStream = compositor?.stream || videoStreamRef.current;
        }
        if (recordStream) {
          try {
            const videoMime = pickVideoRecorderMimeType();
            const videoRecorder = new MediaRecorder(recordStream, {
              mimeType: videoMime,
              videoBitsPerSecond: PITCH_RECORD_VIDEO_BITRATE,
            });
            videoRecorder.ondataavailable = (e) => {
              if (e.data?.size) videoChunksRef.current.push(e.data);
            };
            videoRecorder.onerror = (e) => {
              console.error('Pitch video recorder error:', e);
            };
            videoRecorderRef.current = videoRecorder;
            videoRecorder.start(200);
          } catch (videoErr) {
            console.warn('Pitch video recorder unavailable; saving audio only', videoErr);
            videoRecorderRef.current = null;
            compositor?.releaseRecordStream?.();
          }
        }
      }

      startedAtRef.current = Date.now();
      setRecording(true);
    } catch (err) {
      stopAudioRecording();
      setError(err?.message || 'Microphone permission denied or unavailable');
      throw err;
    }
  }, [withCamera, stopAudioRecording]);

  const stop = useCallback(() => {
    return new Promise((resolve, reject) => {
      const audioRec = audioRecorderRef.current;
      const videoRec = videoRecorderRef.current;
      if (!audioRec) {
        reject(new Error('Not recording'));
        return;
      }

      let audioDone = false;
      let videoDone = !videoRec;
      let settled = false;

      const finalize = () => {
        if (settled) return;
        if (!audioDone || !videoDone) return;
        settled = true;
        clearTimeout(timeoutId);

        const durationSec = Math.max(1, (Date.now() - startedAtRef.current) / 1000);
        const mime = audioRec.mimeType || audioMimeRef.current || pickAudioRecorderMimeType();
        const audioBlob = new Blob(audioChunksRef.current, {
          type: audioBlobTypeFromMime(mime),
        });
        const videoBlob = videoChunksRef.current.length
          ? new Blob(videoChunksRef.current, { type: 'video/webm' })
          : null;

        stopAudioRecording();
        compositorRef.current?.releaseRecordStream?.();
        setRecording(false);

        if (audioBlob.size < 64) {
          reject(new Error('No audio captured — check your microphone and try again'));
          return;
        }
        resolve({ audioBlob, videoBlob, durationSec, audioMime: mime });
      };

      const timeoutId = setTimeout(() => {
        console.warn('Pitch recorder stop timed out; finalizing with captured audio');
        videoDone = true;
        if (!audioDone && audioRec.state === 'recording') {
          try {
            audioRec.requestData();
            audioRec.stop();
          } catch {
            audioDone = true;
            finalize();
          }
          return;
        }
        audioDone = true;
        finalize();
      }, STOP_FINALIZE_TIMEOUT_MS);

      audioRec.onstop = () => {
        audioDone = true;
        finalize();
      };

      if (videoRec) {
        videoRec.onstop = () => {
          videoDone = true;
          finalize();
        };
      }

      try {
        if (audioRec.state === 'recording') {
          audioRec.requestData();
          audioRec.stop();
        } else {
          audioDone = true;
        }

        if (videoRec?.state === 'recording') {
          videoRec.requestData();
          videoRec.stop();
        } else {
          videoDone = true;
        }

        finalize();
      } catch (err) {
        settled = true;
        clearTimeout(timeoutId);
        stopAudioRecording();
        compositorRef.current?.releaseRecordStream?.();
        setRecording(false);
        reject(err);
      }
    });
  }, [stopAudioRecording]);

  return { recording, error, start, stop, previewStream, previewLoading };
}
